import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  FileText,
  Download,
  Search,
  User,
  Building2,
  Calendar,
  MapPin,
  CreditCard,
  Printer,
  Eye,
  Check
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Delega = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [delegaList, setDelegaList] = useState([]);
  
  const [formData, setFormData] = useState({
    nome_completo: '',
    nato_a: '',
    nato_il: '',
    residente_a: '',
    via: '',
    codice_fiscale: '',
    // Checkbox enti
    questura: false,
    questura_luogo: '',
    comune: false,
    comune_luogo: '',
    agenzia_entrate: false,
    agenzia_entrate_luogo: '',
    altro: false,
    altro_descrizione: '',
    // Operazione
    operazione: '',
    luogo_data: '',
  });

  useEffect(() => {
    loadClients();
    loadDeleghe();
  }, []);

  const loadClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadDeleghe = async () => {
    try {
      const response = await axios.get(`${API}/deleghe`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDelegaList(response.data || []);
    } catch (error) {
      console.error('Error loading deleghe:', error);
    }
  };

  const filteredClients = clients.filter(client => {
    if (!searchQuery) return false;
    const query = searchQuery.toLowerCase();
    const fullName = `${client.last_name || ''} ${client.first_name || ''}`.toLowerCase();
    const cf = (client.fiscal_code || '').toLowerCase();
    return fullName.includes(query) || cf.includes(query);
  });

  const selectClient = (client) => {
    setSelectedClient(client);
    setSearchQuery('');
    
    // Auto-populate form
    const fullName = client.client_type === 'azienda' 
      ? client.company_name 
      : `${client.last_name || ''} ${client.first_name || ''}`.trim();
    
    let birthPlace = '';
    if (client.birth_abroad) {
      birthPlace = client.birth_country || '';
    } else {
      birthPlace = client.birth_comune || '';
    }
    
    // Format birth date
    let birthDate = '';
    if (client.birth_date) {
      try {
        const bd = new Date(client.birth_date);
        birthDate = bd.toLocaleDateString('it-IT');
      } catch (e) {}
    }
    
    // Get residence
    let residence = client.city || '';
    let via = client.address || '';
    
    setFormData({
      ...formData,
      nome_completo: fullName,
      nato_a: birthPlace,
      nato_il: birthDate,
      residente_a: residence,
      via: via,
      codice_fiscale: client.fiscal_code || '',
      luogo_data: `Prato, ${new Date().toLocaleDateString('it-IT')}`
    });
  };

  const generatePDF = async () => {
    if (!selectedClient) {
      toast.error('Seleziona un cliente');
      return;
    }
    
    if (!formData.nome_completo || !formData.codice_fiscale) {
      toast.error('Compila i campi obbligatori');
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await axios.post(`${API}/deleghe/generate-pdf`, {
        client_id: selectedClient.id,
        ...formData
      }, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Download PDF
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DELEGA_${formData.nome_completo.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Reload deleghe list to show new record
      loadDeleghe();
      
      toast.success('PDF generato con successo');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore generazione PDF');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <FileText className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Delega per Disbrigo Pratiche</h1>
            <p className="text-slate-500">Genera deleghe per i tuoi clienti</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form Delega */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Nuova Delega
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Ricerca Cliente */}
              <div className="space-y-2">
                <Label>Cerca Cliente</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    data-testid="delega-search-client"
                    placeholder="Cerca per nome o codice fiscale..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                  {/* Dropdown risultati */}
                  {filteredClients.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 bg-white border rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
                      {filteredClients.slice(0, 10).map(client => (
                        <button
                          key={client.id}
                          data-testid={`delega-client-option-${client.id}`}
                          onClick={() => selectClient(client)}
                          className="w-full px-4 py-2 text-left hover:bg-slate-100 flex items-center gap-3"
                        >
                          <User className="w-4 h-4 text-slate-400" />
                          <div>
                            <p className="font-medium">
                              {client.client_type === 'azienda' 
                                ? client.company_name 
                                : `${client.last_name} ${client.first_name}`}
                            </p>
                            <p className="text-xs text-slate-500">{client.fiscal_code}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                {selectedClient && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="text-green-700">
                      Cliente selezionato: <strong>
                        {selectedClient.client_type === 'azienda' 
                          ? selectedClient.company_name 
                          : `${selectedClient.last_name} ${selectedClient.first_name}`}
                      </strong>
                    </span>
                  </div>
                )}
              </div>

              {/* Dati Delegante */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  IL DELEGANTE (PERSONA FISICA)
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Il/La sottoscritto/a *</Label>
                    <Input
                      value={formData.nome_completo}
                      onChange={(e) => setFormData({ ...formData, nome_completo: e.target.value })}
                      placeholder="Nome e Cognome"
                    />
                  </div>
                  
                  <div>
                    <Label>Nato/a a</Label>
                    <Input
                      value={formData.nato_a}
                      onChange={(e) => setFormData({ ...formData, nato_a: e.target.value })}
                      placeholder="Luogo di nascita"
                    />
                  </div>
                  
                  <div>
                    <Label>il</Label>
                    <Input
                      value={formData.nato_il}
                      onChange={(e) => setFormData({ ...formData, nato_il: e.target.value })}
                      placeholder="Data di nascita"
                    />
                  </div>
                  
                  <div>
                    <Label>Residente a</Label>
                    <Input
                      value={formData.residente_a}
                      onChange={(e) => setFormData({ ...formData, residente_a: e.target.value })}
                      placeholder="Comune di residenza"
                    />
                  </div>
                  
                  <div>
                    <Label>in Via</Label>
                    <Input
                      value={formData.via}
                      onChange={(e) => setFormData({ ...formData, via: e.target.value })}
                      placeholder="Indirizzo"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label>Codice Fiscale *</Label>
                    <Input
                      value={formData.codice_fiscale}
                      onChange={(e) => setFormData({ ...formData, codice_fiscale: e.target.value.toUpperCase() })}
                      placeholder="RSSMRA80A01H501Z"
                      className="font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Enti */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  A RAPPRESENTARLO/A PRESSO
                </h3>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <Checkbox
                      id="questura"
                      checked={formData.questura}
                      onCheckedChange={(checked) => setFormData({ ...formData, questura: checked })}
                    />
                    <Label htmlFor="questura" className="flex-shrink-0">Questura di</Label>
                    <Input
                      value={formData.questura_luogo}
                      onChange={(e) => setFormData({ ...formData, questura_luogo: e.target.value })}
                      placeholder="Luogo"
                      className="max-w-xs"
                      disabled={!formData.questura}
                    />
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <Checkbox
                      id="comune"
                      checked={formData.comune}
                      onCheckedChange={(checked) => setFormData({ ...formData, comune: checked })}
                    />
                    <Label htmlFor="comune" className="flex-shrink-0">Comune di</Label>
                    <Input
                      value={formData.comune_luogo}
                      onChange={(e) => setFormData({ ...formData, comune_luogo: e.target.value })}
                      placeholder="Luogo"
                      className="max-w-xs"
                      disabled={!formData.comune}
                    />
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <Checkbox
                      id="agenzia"
                      checked={formData.agenzia_entrate}
                      onCheckedChange={(checked) => setFormData({ ...formData, agenzia_entrate: checked })}
                    />
                    <Label htmlFor="agenzia" className="flex-shrink-0">Agenzia delle Entrate di</Label>
                    <Input
                      value={formData.agenzia_entrate_luogo}
                      onChange={(e) => setFormData({ ...formData, agenzia_entrate_luogo: e.target.value })}
                      placeholder="Luogo"
                      className="max-w-xs"
                      disabled={!formData.agenzia_entrate}
                    />
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <Checkbox
                      id="altro"
                      checked={formData.altro}
                      onCheckedChange={(checked) => setFormData({ ...formData, altro: checked })}
                    />
                    <Label htmlFor="altro" className="flex-shrink-0">Altro:</Label>
                    <Input
                      value={formData.altro_descrizione}
                      onChange={(e) => setFormData({ ...formData, altro_descrizione: e.target.value })}
                      placeholder="Specificare"
                      className="flex-1"
                      disabled={!formData.altro}
                    />
                  </div>
                </div>
              </div>

              {/* Operazione */}
              <div className="space-y-2">
                <Label>PER LA SEGUENTE OPERAZIONE</Label>
                <Textarea
                  value={formData.operazione}
                  onChange={(e) => setFormData({ ...formData, operazione: e.target.value })}
                  placeholder="es: consegna documenti, ritiro certificati, presentazione istanza, ecc."
                  rows={3}
                />
              </div>

              {/* Luogo e Data */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Luogo e Data</Label>
                  <Input
                    value={formData.luogo_data}
                    onChange={(e) => setFormData({ ...formData, luogo_data: e.target.value })}
                    placeholder="Prato, 24/03/2026"
                  />
                </div>
              </div>

              {/* Azioni */}
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  onClick={generatePDF} 
                  disabled={loading || !selectedClient}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="delega-download-pdf"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {loading ? 'Generazione...' : 'Genera e Scarica PDF'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Lista Deleghe Recenti */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Deleghe Recenti</CardTitle>
            </CardHeader>
            <CardContent>
              {delegaList.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-4">Nessuna delega generata</p>
              ) : (
                <div className="space-y-3">
                  {delegaList.slice(0, 30).map(delega => (
                    <div key={delega.id} className="p-3 border rounded-lg hover:bg-slate-50">
                      <p className="font-medium text-sm">{delega.nome_completo}</p>
                      <p className="text-xs text-slate-500">{delega.codice_fiscale}</p>
                      <p className="text-xs text-slate-400 mt-1">{formatDate(delega.created_at)}</p>
                      <button
                        data-testid={`delega-view-pdf-${delega.id}`}
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-2"
                        onClick={async () => {
                          try {
                            const res = await axios.get(`${API}/deleghe/${delega.id}/pdf`, {
                              headers: { Authorization: `Bearer ${token}` },
                              responseType: 'blob'
                            });
                            const url = window.URL.createObjectURL(new Blob([res.data]));
                            const a = document.createElement('a');
                            a.href = url;
                            a.setAttribute('download', `DELEGA_${delega.nome_completo.replace(/\s+/g, '_')}.pdf`);
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            window.URL.revokeObjectURL(url);
                          } catch (err) {
                            toast.error('Errore visualizzazione PDF');
                          }
                        }}
                      >
                        <Download className="w-3 h-3" /> Visualizza PDF
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Delega;
